import React, { useState, useMemo } from "react";
import PageLoader from "../components/ui/PageLoader";
import "../assets/css/style.css";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import Modal from "../components/ui/Modal";
import {
  useGetCompaniesQuery,
  useCreateCompanyMutation,
  useUpdateCompanyMutation,
  useDeleteCompanyMutation,
} from "apis/company";
import { FaPlus } from "react-icons/fa6";
import CompanyEdit from "components/company/CompanyEdit";
import CompanyView from "components/company/CompanyView";
import Table from "components/ui/Table";

const formatABN = (abn) => {
  if (!abn) return null;
  return abn.replace(/(\d{2})(\d{3})(\d{3})(\d{3})/, "$1 $2 $3 $4");
};

const Company = () => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  // Table page size
  const pageSize = 10;

  const {
    data,
    isPending: isDataPending,
    isError: isDataError,
    error: dataError,
  } = useGetCompaniesQuery();

  const { mutate: createCompany, isLoading: isCreateLoading } =
    useCreateCompanyMutation();

  const { mutate: updateCompany } = useUpdateCompanyMutation();

  const { mutate: deleteCompany, isLoading: isDeleteLoading } =
    useDeleteCompanyMutation();

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "name",
        header: "Company Name",
        isFilter: true,
        minWidth: "180px",
      },
      {
        accessorKey: "organizationId.name",
        header: "Organization",
        cell: (row) => <span>{row.organizationId?.name || "-"}</span>,
        isFilter: true,
        minWidth: "150px",
      },
      {
        accessorKey: "primaryOwner.name",
        header: "Owner",
        cell: (row) => <span>{row.primaryOwner?.name || "-"}</span>,
        isFilter: true,
      },
      {
        accessorKey: "licenseeInCharge.name",
        header: "Licensee in Charge",
        cell: (row) => <span>{row.licenseeInCharge?.name || "-"}</span>,
        isFilter: true,
        minWidth: "160px",
      },
      {
        accessorKey: "abn",
        header: "ABN",
        cell: (row) => <span>{formatABN(row.abn) || "-"}</span>,
        isFilter: true,
      },
      {
        accessorKey: "isEnterprise",
        header: "Type",
        cell: (row) => (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              row.isEnterprise
                ? "bg-blue-100 text-blue-800"
                : "bg-lightgray text-darkergray"
            }`}
          >
            {row.isEnterprise ? "Enterprise" : "Standard"}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (row) => {
          const status = row.status || "active";
          const statusStyles = {
            active: "bg-green-100 text-green-800",
            pending: "bg-yellow-100 text-yellow-800",
            suspended: "bg-red-100 text-red-800",
            inactive: "bg-lightgray text-darkergray",
          };
          return (
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyles[status]}`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
          );
        },
        isFilter: false,
      },
      {
        accessorKey: "requiredDocuments",
        header: "Agreement",
        cell: (row) => {
          const required = row.requiredDocuments?.length || 0;
          const signed = row.agreedTerms?.length || 0;

          if (required === 0) {
            return <span className="text-sm text-darkgray">-</span>;
          }

          const isComplete = signed >= required;
          return (
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${
                isComplete
                  ? "bg-green-100 text-green-800"
                  : "bg-yellow-100 text-yellow-800"
              }`}
            >
              {isComplete ? "Completed" : `${signed}/${required}`}
            </span>
          );
        },
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "offices",
        header: "Offices",
        cell: (row) => (
          <span className="text-center">{row.offices?.length || 0}</span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "createdAt",
        header: "Created",
        cell: (row) => (
          <span className="text-sm">
            {new Date(row.createdAt).toLocaleDateString("en-AU", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "actions",
        header: "Actions",
        cell: (row) => (
          <div className="flex items-center space-x-4">
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsEditOpen(true);
              }}
              className="edit-icon"
              title="Edit Company"
            >
              <FiEdit2 fontSize={20} />
            </button>
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsDeleteOpen(true);
              }}
              className="delete-icon"
              title="Delete Company"
            >
              <FiTrash2 fontSize={20} />
            </button>
          </div>
        ),
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        minWidth: "100px",
      },
    ],
    [],
  );

  // ── Loading / error states ────────────────────────────────────────────────
  if (isDataPending) {
    return <PageLoader />;
  }

  if (isDataError) {
    return (
      <div className="container mx-auto">
        <div className="text-center py-8">
          <p className="text-red-600">
            Error loading companies: {dataError?.message}
          </p>
        </div>
      </div>
    );
  }

  const selectedCompany = data?.find((company) => company._id === selectedId);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleEditSubmit = (formData) => {
    updateCompany({ id: selectedId, ...formData });
    setIsEditOpen(false);
  };

  const handleCreateSubmit = (formData) => {
    createCompany(formData);
    setIsAddOpen(false);
  };

  const handleDelete = () => {
    deleteCompany(selectedId);
    setIsDeleteOpen(false);
  };

  const handleViewRowClick = (row) => {
    setSelectedId(row._id);
    setIsEditOpen(true);
  };

  // Passed as ButtonCmp — renders in the top-right of the Table toolbar
  const AddCompanyButton = () => (
    <button
      className="black-button flex items-center"
      onClick={() => setIsAddOpen(true)}
      disabled={isCreateLoading}
    >
      <FaPlus size={14} className="mr-2" />
      Add Company
    </button>
  );

  return (
    <>
      {/* Edit Modal */}
      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Edit Company"
        className="max-w-3xl"
      >
        <CompanyEdit
          initialData={selectedCompany}
          onSubmit={handleEditSubmit}
        />
      </Modal>

      {/* Add Modal */}
      <Modal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title="Add Company"
        className="max-w-3xl"
      >
        <CompanyEdit initialData={{}} onSubmit={handleCreateSubmit} />
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        title="Delete Company"
      >
        <div>
          <p className="mb-4">
            Are you sure you want to delete "{selectedCompany?.name}"? This
            action cannot be undone.
          </p>
          <div className="w-full flex gap-2 justify-end py-4">
            <button
              onClick={() => setIsDeleteOpen(false)}
              className="gray-button"
              disabled={isDeleteLoading}
            >
              No
            </button>
            <button
              onClick={handleDelete}
              className="black-button"
              disabled={isDeleteLoading}
            >
              Yes
            </button>
          </div>
        </div>
      </Modal>

      {/* View Modal */}
      <Modal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        title="Company Details"
        className="max-w-4xl"
      >
        <CompanyView company={selectedCompany} />
      </Modal>

      {/* Table */}
      <div className="container mx-auto">
        <Table
          title="Companies"
          columns={columns}
          data={data || []}
          pageSize={pageSize}
          ButtonCmp={AddCompanyButton}
          onRowClick={handleViewRowClick}
        />
      </div>
    </>
  );
};

export default Company;